import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const VERB_COLOUR = {
  created: "bg-blue-100 text-blue-700",
  "moved to done": "bg-emerald-100 text-emerald-700",
  "moved to in progress": "bg-amber-100 text-amber-700",
  "moved to review": "bg-violet-100 text-violet-700",
  "moved to todo": "bg-gray-100 text-gray-600",
  sent: "bg-blue-100 text-blue-700",
  paid: "bg-emerald-100 text-emerald-700",
  voided: "bg-rose-100 text-rose-700",
  commented: "bg-gray-100 text-gray-600",
  added: "bg-violet-100 text-violet-700",
};

const verbColour = (verb) => {
  for (const [k, v] of Object.entries(VERB_COLOUR)) {
    if (verb?.toLowerCase().includes(k)) return v;
  }
  return "bg-gray-100 text-gray-600";
};

const entityIcon = (type) => {
  const m = { task: "T", invoice: "£", member: "👤", document: "D", comment: "C" };
  return m[type] || "·";
};

const fmtRelative = (iso) => {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

const ActivityFeed = ({ projectId, limit = 30 }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = projectId
        ? `/projects/${projectId}/activity?limit=${limit}`
        : `/activity?limit=${limit}`;
      const { data } = await api.get(url);
      setEvents(data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [projectId, limit]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="py-6 text-center text-xs text-gray-400">Loading…</div>;
  }

  if (events.length === 0) {
    return <div className="py-8 text-center text-sm text-gray-400">No activity yet.</div>;
  }

  return (
    <div className="divide-y divide-gray-100">
      {events.map((ev) => (
        <div key={ev.event_id} className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50">
          <div className="w-7 h-7 flex-shrink-0 rounded-full bg-gray-100 flex items-center justify-center text-[11px] text-gray-500 mt-0.5">
            {entityIcon(ev.entity_type)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-gray-900 font-medium truncate">{ev.actor_name}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-[0.1em] ${verbColour(ev.verb)}`}>
                {ev.verb}
              </span>
            </div>
            <div className="text-xs text-gray-500 mt-0.5 truncate">{ev.title}</div>
          </div>
          <div className="text-[10px] text-gray-400 flex-shrink-0 mt-1 font-mono whitespace-nowrap">
            {fmtRelative(ev.created_at)}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ActivityFeed;
